import React, { memo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { DatePicker } from '@material-ui/pickers';
import clsx from 'clsx';
import { Divider } from '@material-ui/core';
import { useControlled } from '@material-ui/core/utils';
import { withStyles, fade } from '@material-ui/core/styles';
import { isBefore, isSameDay, isWithinInterval, isValid } from 'date-fns';
import { currentMonth, monthForward } from 'helpers/dates';
import { isNil } from 'lodash';

export const styles = (theme) => ({
  /* Styles applied to the root element. */
  root: {
    display: 'inline-flex',
    backgroundColor: '#fff',
    padding: '20px 30px',

    '& .MuiPickersDay-day': {
      margin: '0',

      '&.MuiPickersDay-daySelected': {
        '&.MuiPickersDay-dayDisabled': {
          color: fade(theme.palette.background.dark.contrastText, 0.5),
        },
      },
    },

    '& .MuiPickersBasePicker-pickerView': {
      minWidth: '100%',
      padding: '0',
    },

    '& .MuiPickersStaticWrapper-staticWrapperRoot': {
      minWidth: 'initial',
    },

    '& .MuiPickersCalendar-week > div': {
      flex: '1',
      textAlign: 'center',
    },

    '& .MuiPickersCalendarHeader-daysHeader': {
      maxHeight: 'none',

      '&  > span': {
        flex: '1',
        lineHeight: 1,
      },
    },
  },

  wrapper: { flex: 1 },
  wrapperStart: {},
  wrapperEnd: {},
  wrapperSingle: {},

  divider: {
    margin: '0 30px',
    alignSelf: 'stretch',
    height: 'auto',
  },

  dayWrapper: {
    position: 'relative',
    margin: '2px 0',
    padding: '0 4px',
  },

  isSelectingSingleView: {
    '& .MuiPickersDay-day': {
      position: 'relative',

      '&::before': {
        content: '""',
        position: 'absolute',
        top: '3%',
        left: '3%',
        width: '94%',
        height: '94%',
        pointerEvents: 'none',
        border: `1px solid ${theme.palette.primary.main}`,
      },
    },
  },

  isDayBetween: {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: fade(theme.palette.primary.light, 0.4),
    },
  },

  isStartDay: {
    '&::before': {
      width: '50%',
      right: 0,
      left: 'auto',
    },
  },

  isEndDay: {
    '&::before': {
      width: '50%',
    },

    '&$isStartDay::before': {
      display: 'none',
    },
  },

  isDayOutOfThisMonth: {
    opacity: 0,
  },

  isSelected: {},
});

const CHOOSING_DATE_START = 'start';
const CHOOSING_DATE_END = 'end';

const DateRangePicker = memo(function DateRangePicker(props) {
  const {
    className,
    classes,
    value: valueProp,
    onChange: onChangeProp,
    singleView,
    component: Component = 'div',
    ...rest
  } = props;

  const [choosingDate, setChoosingDate] = useState(CHOOSING_DATE_START);

  const [[startDate, endDate], setValue] = useControlled({
    controlled: valueProp,
    default: [currentMonth, monthForward],
    state: 'dateRange',
    name: 'DateRangePicker',
  });

  const onStartChange = useCallback(
    (date) => {
      const v = [startDate, endDate];

      let result = [...v];

      const isDateAfter = !isNil(v[1]) && !isBefore(date, v[1]);

      if (isDateAfter) {
        result = [date, date];
      } else {
        result = [date, v[1]];
      }

      setValue(result);
      onChangeProp?.(result);
    },
    [endDate, onChangeProp, setValue, startDate],
  );

  const onEndChange = useCallback(
    (date) => {
      const v = [startDate, endDate];
      let result = [...v];

      const isDateBefore = isBefore(date, v[0]);

      if (isDateBefore) {
        result = [date, v[0]];
      } else {
        result = [v[0], date];
      }

      setValue(result);
      onChangeProp?.(result);
    },
    [endDate, onChangeProp, setValue, startDate],
  );

  const onSingleViewChange = useCallback(
    (date) => {
      if (choosingDate === CHOOSING_DATE_START) {
        onStartChange(date);
        setChoosingDate(CHOOSING_DATE_END);
      } else if (choosingDate === CHOOSING_DATE_END) {
        onEndChange(date);
        setChoosingDate(CHOOSING_DATE_START);
      }
    },
    [choosingDate, onStartChange, onEndChange],
  );

  return (
    <Component className={clsx(classes.root, className)}>
      {!singleView && (
        <>
          <div className={clsx(classes.wrapper, classes.wrapperStart)}>
            <DatePicker
              disableToolbar
              autoOk
              variant="static"
              value={startDate}
              onChange={onStartChange}
              renderDay={createRenderDay('start', {
                startDate,
                endDate,
                singleView,
                choosingDate,
                classes,
              })}
              data-picker="start"
              {...rest}
            />
          </div>

          <Divider className={classes.divider} orientation="vertical" />

          <div className={clsx(classes.wrapper, classes.wrapperEnd)}>
            <DatePicker
              disableToolbar
              autoOk
              variant="static"
              value={endDate}
              onChange={onEndChange}
              renderDay={createRenderDay('end', {
                startDate,
                endDate,
                singleView,
                choosingDate,
                classes,
              })}
              data-picker="end"
              {...rest}
            />
          </div>
        </>
      )}

      {singleView && (
        <div className={clsx(classes.wrapper, classes.wrapperSingle)}>
          <DatePicker
            disableToolbar
            autoOk
            variant="static"
            value={choosingDate === CHOOSING_DATE_START ? startDate : endDate}
            onChange={onSingleViewChange}
            renderDay={createRenderDay('single', {
              startDate,
              endDate,
              singleView,
              choosingDate,
              classes,
            })}
            data-picker="single"
            {...rest}
          />
        </div>
      )}
    </Component>
  );
});

const createRenderDay =
  (type, { startDate, endDate, singleView, choosingDate, classes }) =>
  (thisDayDate, selectedDate, isDayInThisMonth, nativeDayArg) => {
    const isStartDay = isSameDay(thisDayDate, startDate);
    const isEndDay = isSameDay(thisDayDate, endDate);

    const isEmptyEndDay = type === 'end' && !endDate;
    const isEmptyStartDay = type === 'start' && !startDate;

    let isDayBetween;

    if (
      isValid(startDate) &&
      isValid(endDate) &&
      isBefore(startDate, endDate)
    ) {
      try {
        isDayBetween =
          isWithinInterval(thisDayDate, {
            start: startDate,
            end: endDate,
          }) || isStartDay;
      } catch (e) {
        console.warn(e);
      }
    }

    const isSelected =
      !isEmptyStartDay &&
      !isEmptyEndDay &&
      (nativeDayArg.selected || isStartDay || isEndDay);
    const nativeDay = React.cloneElement(nativeDayArg, {
      selected: isSelected,
    });

    const wrapperClasses = clsx(classes.dayWrapper, {
      [classes.isSelected]: isSelected,
      [classes.isDayBetween]: isDayBetween,

      [classes.isStartDay]: isStartDay,
      [classes.isEndDay]: isEndDay,
      [classes.isSelectingSingleView]:
        singleView &&
        ((isStartDay && choosingDate === CHOOSING_DATE_START) ||
          (isEndDay && choosingDate === CHOOSING_DATE_END)),
      [classes.isDayOutOfThisMonth]: !isDayInThisMonth,
    });

    return (
      // eslint-disable-next-line react/destructuring-assignment
      <div className={wrapperClasses} data-day={thisDayDate.getDate()}>
        {nativeDay}
      </div>
    );
  };

DateRangePicker.propTypes = {
  component: PropTypes.node,
  className: PropTypes.string,
  classes: PropTypes.object,
  value: PropTypes.array,
  onChange: PropTypes.func,
  singleView: PropTypes.bool,
};

export default withStyles(styles, { name: 'MuiDateRangePicker' })(
  DateRangePicker,
);
